/**
 * Passive debris tracers. CPU reference implementation.
 *
 * Structure of arrays in Float32Arrays. Phase 1 is pure advection: tracer
 * velocity equals the local wind. Integration is explicit RK2 (midpoint):
 *
 *   k1 = u(x, t)
 *   k2 = u(x + dt/2 * k1, t + dt/2)
 *   x <- x + dt * k2
 *
 * Midpoint RK2 has O(dt^2) global error. Forward Euler on a circular orbit
 * spirals outward every step; midpoint keeps the drift small enough to see
 * clean orbits at the default time scale.
 *
 * Tracers that leave the domain or exceed max age are respawned in the spawn
 * annulus from the seeded RNG. Domain and annulus are measured from the
 * storm center, which moves when the storm translates. Nothing is
 * allocated after construction.
 *
 * A GPU compute version comes in phase 4 and must match this one on a fixed seed.
 */
import { Rng } from '../core/rng';
import type { WindField, WindSample } from './windField';

export interface TracerOptions {
  capacity: number;
  seed: number;
  domainRadiusM: number;
  spawnInnerM: number;
  spawnOuterM: number;
  spawnMinHeightM: number;
  spawnMaxHeightM: number;
  maxAgeS: number;
}

export interface Tracers {
  readonly capacity: number;
  /** Active tracers occupy indices [0, count). */
  count: number;
  readonly posX: Float32Array;
  readonly posY: Float32Array;
  readonly posZ: Float32Array;
  readonly velX: Float32Array;
  readonly velY: Float32Array;
  readonly velZ: Float32Array;
  readonly age: Float32Array;
  /** Positions before the last step, for render interpolation. */
  readonly prevX: Float32Array;
  readonly prevY: Float32Array;
  readonly prevZ: Float32Array;
  /** Set the active count, spawning any newly activated tracers. */
  setCount(n: number): void;
  step(dtS: number, tS: number, field: WindField): void;
  respawnAll(): void;
  readonly options: TracerOptions;
}

export function createTracers(options: TracerOptions): Tracers {
  const n = options.capacity;
  const rng = new Rng(options.seed);
  const k1: WindSample = { x: 0, y: 0, z: 0 };
  const k2: WindSample = { x: 0, y: 0, z: 0 };
  // Storm center as of the last step; spawns are placed around it.
  const center: WindSample = { x: 0, y: 0, z: 0 };

  const t: Tracers = {
    capacity: n,
    count: 0,
    posX: new Float32Array(n),
    posY: new Float32Array(n),
    posZ: new Float32Array(n),
    velX: new Float32Array(n),
    velY: new Float32Array(n),
    velZ: new Float32Array(n),
    age: new Float32Array(n),
    prevX: new Float32Array(n),
    prevY: new Float32Array(n),
    prevZ: new Float32Array(n),
    options,
    setCount(next: number): void {
      const clamped = next < 0 ? 0 : next > n ? n : next;
      for (let i = t.count; i < clamped; i++) spawn(i, true);
      t.count = clamped;
    },
    respawnAll(): void {
      for (let i = 0; i < t.count; i++) spawn(i, true);
    },
    step(dtS: number, tS: number, field: WindField): void {
      const { posX, posY, posZ, velX, velY, velZ, age, prevX, prevY, prevZ } = t;
      const r2Max = options.domainRadiusM * options.domainRadiusM;
      const halfDt = 0.5 * dtS;
      field.stormCenter(tS + dtS, center);
      const ccx = center.x;
      const ccz = center.z;
      for (let i = 0; i < t.count; i++) {
        const x = posX[i] as number;
        const y = posY[i] as number;
        const z = posZ[i] as number;
        prevX[i] = x;
        prevY[i] = y;
        prevZ[i] = z;

        field.sample(x, y, z, tS, k1);
        field.sample(x + halfDt * k1.x, y + halfDt * k1.y, z + halfDt * k1.z, tS + halfDt, k2);

        const nx = x + dtS * k2.x;
        const ny = y + dtS * k2.y;
        const nz = z + dtS * k2.z;
        posX[i] = nx;
        posY[i] = ny;
        posZ[i] = nz;
        velX[i] = k2.x;
        velY[i] = k2.y;
        velZ[i] = k2.z;
        const a = (age[i] as number) + dtS;
        age[i] = a;

        const ox = nx - ccx;
        const oz = nz - ccz;
        if (a > options.maxAgeS || ox * ox + oz * oz > r2Max || ny < 0) spawn(i, false);
      }
    },
  };

  /** Uniform-by-area point in the annulus, random height, staggered age. */
  function spawn(i: number, staggerAge: boolean): void {
    const r0 = options.spawnInnerM;
    const r1 = options.spawnOuterM;
    const r = Math.sqrt(rng.range(r0 * r0, r1 * r1));
    const theta = rng.range(0, 2 * Math.PI);
    const x = center.x + r * Math.cos(theta);
    const z = center.z + r * Math.sin(theta);
    const y = rng.range(options.spawnMinHeightM, options.spawnMaxHeightM);
    t.posX[i] = x;
    t.posY[i] = y;
    t.posZ[i] = z;
    // Snap prev to the new position so interpolation does not draw a streak.
    t.prevX[i] = x;
    t.prevY[i] = y;
    t.prevZ[i] = z;
    t.velX[i] = 0;
    t.velY[i] = 0;
    t.velZ[i] = 0;
    // Staggering at startup keeps all tracers from respawning on the same frame.
    t.age[i] = staggerAge ? rng.range(0, options.maxAgeS) : 0;
  }

  return t;
}
